import { useMemo, useState } from 'react'
import { MagnifyingGlass, UserCircle, Phone } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import type { RosterFarmer } from '@/offline'
import { Banner, Button, Card, CardBody, Field, Input } from '@/ui'
import { findFarmers } from '../../model/search'
import type { DeliveryDraft } from '../../model/delivery'
import { BiText } from '../../components/GateScreen'

export function FarmerPhoto({ url, size = 64 }: { url: string | null | undefined; size?: number }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-card border border-rule bg-paper-sunk text-muted"
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        <UserCircle size={size * 0.6} />
      </span>
    )
  }
  return (
    <img
      src={url}
      alt=""
      onError={() => setFailed(true)}
      className="shrink-0 rounded-card border border-rule object-cover"
      style={{ width: size, height: size }}
    />
  )
}

/**
 * F4.2 — किसान खोजें · Find farmer. Name (Hindi or English) or the printed roster number, the
 * phone's last 4 digits, and the photo check. Tapping No leaves no draft behind.
 */
export function FindFarmer({
  farmers,
  villageName,
  onConfirm,
  busy,
}: {
  farmers: readonly RosterFarmer[]
  villageName: string
  onConfirm: (f: RosterFarmer, identification: DeliveryDraft['identification']) => void
  busy?: boolean
}) {
  const { t } = useI18n()
  const [name, setName] = useState('')
  const [rosterNo, setRosterNo] = useState('')
  const [last4, setLast4] = useState('')
  const [picked, setPicked] = useState<{
    farmer: RosterFarmer
    shown: number
    method: 'SEARCH' | 'ROSTER_NO'
  } | null>(null)

  const { results, method } = useMemo(
    () => findFarmers(farmers, { name, rosterNo, last4 }),
    [farmers, name, rosterNo, last4],
  )
  const searched = name.trim() !== '' || rosterNo.trim() !== ''

  if (picked) {
    const f = picked.farmer
    const noPhone = !f.phone_last4
    const typed = /^\d{4}$/.test(last4)
    const phoneMatch: DeliveryDraft['identification']['phone_match'] = noPhone
      ? 'NO_PHONE'
      : typed && last4 === f.phone_last4
        ? 'MATCHED'
        : 'MISMATCH'
    return (
      <div className="flex flex-col gap-4">
        <Card>
          <CardBody className="flex flex-col items-center gap-3 text-center">
            <FarmerPhoto url={f.photo_url} size={168} />
            <div>
              <div className="text-[22px] font-semibold leading-tight">{f.name}</div>
              {f.father_name && (
                <div className="text-[15px] text-muted">
                  {t('gate.farmer.careOf', { name: f.father_name })}
                </div>
              )}
              <div className="mt-1 font-mono text-[14px]">
                {t('gate.farmer.rosterNo')} {f.roster_no} · {villageName}
              </div>
            </div>
          </CardBody>
        </Card>
        {noPhone && <Banner tone="info">{t('gate.farmer.noPhone')}</Banner>}
        {!noPhone && (
          <Field label={t('gate.farmer.last4')} required hint={t('gate.farmer.last4Hint')}>
            <Input
              size="touch"
              inputMode="numeric"
              maxLength={4}
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="font-mono"
              placeholder="0000"
            />
          </Field>
        )}
        {!noPhone && typed && phoneMatch === 'MISMATCH' && (
          <Banner tone="warn">{t('gate.farmer.phoneMismatch')}</Banner>
        )}
        <p className="text-center text-[16px] font-semibold">{t('gate.farmer.isThisHim')}</p>
        <div className="grid grid-cols-2 gap-2">
          <Button size="xl" variant="secondary" onClick={() => setPicked(null)}>
            <BiText k="gate.farmer.no" />
          </Button>
          <Button
            size="xl"
            loading={busy}
            disabled={!noPhone && !typed}
            onClick={() =>
              onConfirm(f, {
                method: picked.method,
                phone_match: phoneMatch,
                candidates_shown: picked.shown,
              })
            }
          >
            <BiText k="gate.farmer.yes" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3">
        <Field label={t('gate.farmer.name')} hint={t('gate.farmer.nameHint')}>
          <Input
            size="touch"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (e.target.value) setRosterNo('')
            }}
            leading={<MagnifyingGlass />}
            autoComplete="off"
            data-autofocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('gate.farmer.last4')}>
            <Input
              size="touch"
              inputMode="numeric"
              maxLength={4}
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              leading={<Phone />}
              className="font-mono"
            />
          </Field>
          <Field label={t('gate.farmer.rosterNo')}>
            <Input
              size="touch"
              inputMode="numeric"
              value={rosterNo}
              onChange={(e) => {
                setRosterNo(e.target.value.replace(/[^\dA-Za-z-]/g, ''))
                if (e.target.value) setName('')
              }}
              className="font-mono"
            />
          </Field>
        </div>
      </div>

      {searched && results.length === 0 && (
        <Banner tone="warn">{t('gate.farmer.notOnList')}</Banner>
      )}
      <ul className="flex flex-col gap-2">
        {results.slice(0, 20).map((f) => (
          <li key={f.user_id}>
            <button
              type="button"
              onClick={() => setPicked({ farmer: f, shown: results.length, method })}
              className="flex min-h-touch-lg w-full items-center gap-3 rounded-card border border-rule bg-surface px-3 py-2 text-left hover:border-ink/40"
            >
              <FarmerPhoto url={f.photo_url} size={56} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[17px] font-semibold">{f.name}</span>
                {f.father_name && (
                  <span className="block truncate text-[13px] text-muted">
                    {t('gate.farmer.careOf', { name: f.father_name })}
                  </span>
                )}
              </span>
              <span className="text-right font-mono text-[13px] text-muted">
                <span className="block">#{f.roster_no}</span>
                <span className="block">
                  {f.phone_last4 ? `••${f.phone_last4}` : t('gate.farmer.noPhoneShort')}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
